import React, { useCallback, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { KeyboardDatePickerProps } from '@material-ui/pickers';
import {
  IsoDateType,
  dateToIsoDateFormat,
  useDateTimeFormat,
  isValidDate,
  ThreeLetterDateFormats
} from '@utmost.co/utmost-design-system';
import { ComponentPropBase } from '../types';
import { objectUtil } from '../forms/utils/objectUtil';
import { errorValue } from '../atoms/types';
import { DatePickerAtom } from '../atoms/DatePickerAtom/DatePickerAtom';
import { useSmartFormContext } from '../forms/utils/useSmartFormContext';
import { MatchHintSlot } from './MatchHintSlot';

// Keep Props type to absolute minimum to ease maintenance.
type Props = Pick<
  KeyboardDatePickerProps,
  'disableFuture' | 'disablePast' | 'maxDate' | 'maxDateMessage' | 'minDate' | 'minDateMessage' | 'readOnly'
> &
  Omit<ComponentPropBase, 'touched' | 'type'> & {
    dateFormat?: ThreeLetterDateFormats;
    keyboard?: boolean;
    mapInputValue?: (value: any) => any;
    onChange?: (date: IsoDateType, setValue: (value: any) => void) => void;
    showDateIcon?: boolean;
    inputDataTest?: string;
  };

export const DatePicker: React.FC<Props> = ({
  dateFormat,
  errors,
  helpText,
  mapInputValue,
  name,
  onChange,
  ...rest
}) => {
  const [input, meta, helpers] = useField(name || 'INVALID');
  const formikContext = useFormikContext();
  const { translations } = useSmartFormContext();
  const [error, setError] = useState<string | React.ReactNode | null>(null);

  const fieldError = error || errorValue(meta.error) || errorValue(objectUtil.getIn(errors, name));

  const { dateTimeFormat } = useDateTimeFormat(dateFormat);

  const handleOnChange = useCallback(
    (date: MaterialUiPickersDate): void => {
      if (!isValidDate(date)) {
        setError(translations?.invalidDateLabel || 'Invalid date');
        if (onChange) {
          onChange('', helpers.setValue);
        } else {
          helpers.setValue('');
        }
      } else {
        setError(null);
        if (onChange) {
          onChange(dateToIsoDateFormat(date), helpers.setValue);
        } else {
          helpers.setValue(dateToIsoDateFormat(date));
        }
      }
    },
    [helpers, translations, onChange]
  );

  const handleOnBlur = useCallback(
    (event: any) => {
      helpers.setTouched(true);
      input.onBlur(event);
    },
    [helpers, input]
  );

  // Reset touched to false on (re-)focus so a subsequent blur is a fresh false->true transition
  // watchFieldKeysForBlur consumers can detect, instead of staying permanently true after the first blur.
  const handleOnFocus = useCallback(() => {
    helpers.setTouched(false, false);
  }, [helpers]);

  const hasSubmitted = formikContext.submitCount > 0;

  return (
    <div data-test={`${name}-field-control`} className="field-control">
      <MatchHintSlot name={name}>
        <DatePickerAtom
          data-test={`${name}-field`}
          dateFormat={dateFormat}
          infoText={rest.infoText}
          error={hasSubmitted && !!fieldError}
          format={dateTimeFormat.short}
          helperText={hasSubmitted && fieldError ? fieldError : helpText}
          id={input.name}
          key={name}
          name={name}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          placeholder={dateTimeFormat.short.toUpperCase()}
          // mapInputValue only required for Custom Fields as they use
          // a nested value within the input.value object i.e. input.value.value
          value={(mapInputValue ? mapInputValue(input.value) : input.value) || null}
          {...rest}
        />
      </MatchHintSlot>
    </div>
  );
};
